import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare, Sparkles, Compass, Users, Home } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  return (
    <div style={{ minHeight: "100vh", background: "#FFFBC9", color: "var(--folk-text)" }}>
      {/* Brand header FOLK ... OBJECTS with sign-in tucked under OBJECTS */}
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "28px 48px 0" }}>
        <Logo variant="split" size={24} testId="landing-brand-header" />
        <div className="landing-signin-wrap">
          <Link
            to="/portal-login"
            data-testid="client-signin-link"
            style={{
              padding: "8px 16px", borderRadius: 999,
              border: "1.5px solid rgba(27, 74, 102, 0.25)",
              background: "#BADDF0", color: "#0F3A54",
              textDecoration: "none", fontFamily: "Host Grotesk, sans-serif",
              fontSize: 13, fontWeight: 500,
            }}
          >
            Homeowner sign in
          </Link>
        </div>
      </div>

      {/* --- SECTION 1: HERO --- */}
      <section
        data-testid="landing-hero"
        className="landing-section-center landing-hero"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "72px 48px 96px",
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 32,
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600 }}>
          Folk Studio
        </div>
        <h1
          className="folk-serif"
          data-testid="landing-headline"
          style={{
            fontSize: "clamp(36px, 5vw, 68px)",
            lineHeight: 1.05,
            margin: 0,
            maxWidth: 900,
          }}
        >
          A whole new way to shape <em style={{ fontStyle: "italic" }}>spaces</em>.
        </h1>
        <p
          data-testid="landing-subhead"
          style={{
            fontFamily: "Host Grotesk, sans-serif",
            fontSize: "clamp(17px, 1.6vw, 22px)",
            lineHeight: 1.55,
            color: "#2A2828",
            maxWidth: 820,
            margin: 0,
          }}
        >
          A platform managed by us and you. Real designers, a real project management team,
          easy communication and a live working interface. Because designing a home takes real
          work, real craft and real people, and you shouldn't have to chase them across a
          hundred WhatsApp threads.
        </p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12 }} className="landing-cta-row">
          <Link
            to="/survey"
            data-testid="landing-start-survey-btn"
            className="folk-pill folk-pill-lg"
            style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
          >
            Start with your design survey →
          </Link>
          <a
            href="#how"
            data-testid="landing-learn-more"
            style={{
              padding: "16px 28px", borderRadius: 999,
              border: "1.5px solid var(--folk-accent)", background: "transparent",
              color: "var(--folk-accent)", fontFamily: "Host Grotesk, sans-serif",
              fontWeight: 500, fontSize: 15.5, textDecoration: "none",
            }}
          >
            How it works
          </a>
        </div>
      </section>

      {/* --- SECTION 2: VALUE STRIP --- */}
      <section
        id="how"
        data-testid="landing-value-strip"
        style={{
          background: "#BADDF0",
          borderTop: "1px solid rgba(15, 58, 84, 0.14)",
          borderBottom: "1px solid rgba(15, 58, 84, 0.14)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "88px 48px" }} className="landing-section-center">
          <div style={{ fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 600, marginBottom: 18 }}>
            What Folk Studio does
          </div>
          <h2
            className="folk-serif"
            style={{
              fontSize: "clamp(28px, 4vw, 44px)",
              lineHeight: 1.15,
              margin: "0 0 56px",
              maxWidth: 720,
              color: "#0F3A54",
            }}
          >
            An <em style={{ fontStyle: "italic" }}>interior design studio</em>, condensed into a platform that actually feels human.
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>
            <FeatureCard
              index="01"
              title={<>It gets <em style={{ fontStyle: "italic" }}>you</em>.</>}
              body="You take one honest survey. We turn it into a persona that guides every material, layout and mood board, your home, in your own words."
            />
            <FeatureCard
              index="02"
              title={<>Real <em style={{ fontStyle: "italic" }}>designers</em>, real PMC.</>}
              body="A working team behind you: designers, a project management crew, sourcing partners. Not a chatbot, not a template. People who show up."
            />
            <FeatureCard
              index="03"
              title={<>Updated, <em style={{ fontStyle: "italic" }}>live</em>.</>}
              body="Every render, floor plan, sourcing update and phase change lands in your portal the moment your Folk team posts it. Refresh not required."
            />
            <FeatureCard
              index="04"
              title={<>All in one <em style={{ fontStyle: "italic" }}>place</em>.</>}
              body="Moodboards, timelines, layouts, renders, notes and every decision made together, one shared home for the whole project. Nothing scattered, nothing lost."
            />
          </div>
        </div>
      </section>

      {/* --- SECTION 3: 5-STEP JOURNEY + CTA --- */}
      <section
        data-testid="landing-journey"
        style={{ background: "#FFFBC9", padding: "96px 24px" }}
      >
        <div
          className="landing-section-center"
          style={{ maxWidth: 1180, margin: "0 auto" }}
        >
          <div style={{ fontSize: 12, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600, marginBottom: 14 }}>
            The journey
          </div>
          <h2
            className="folk-serif"
            style={{ fontSize: "clamp(30px, 4.4vw, 48px)", lineHeight: 1.1, margin: "0 0 12px", color: "var(--folk-text)", maxWidth: 780 }}
          >
            five steps, from your voice to your <em style={{ fontStyle: "italic" }}>space</em>.
          </h2>
          <p style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15.5, lineHeight: 1.6, color: "#5A5757", maxWidth: 640, margin: "0 0 64px" }}>
            One quiet survey unfolds into a whole home. Here's how we get from your first honest answer to install day.
          </p>

          <ol className="folk-journey" data-testid="journey-timeline">
            {JOURNEY_STEPS.map((s, i) => (
              <JourneyStep
                key={i}
                num={String(i + 1).padStart(2, "0")}
                title={s.title}
                body={s.body}
                Icon={s.icon}
                isLast={i === JOURNEY_STEPS.length - 1}
              />
            ))}
          </ol>

          <div style={{ marginTop: 72, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }} className="landing-cta-row">
            <Link
              to="/survey"
              data-testid="landing-step-one-cta"
              className="folk-pill folk-pill-lg"
              style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
            >
              Begin the survey →
            </Link>
            <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "var(--folk-muted)" }}>
              Takes about 10–15 focused minutes, and shapes a space you'll live in for years.
            </div>
          </div>
        </div>
      </section>

      <DesignerLeadSection />

      {/* Footer */}
      <footer style={{ padding: "40px 48px 60px", textAlign: "center" }}>
        <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "var(--folk-muted)" }}>
          © {new Date().getFullYear()} Folk Objects, a place made yours.
        </div>
      </footer>
    </div>
  );
}

const JOURNEY_STEPS = [
  {
    title: "we begin with you.",
    body: "One honest survey covering morning rituals, how you host, what quietly bothers you about your current space. Every answer is a brick.",
    icon: MessagesSquare,
  },
  {
    title: "we build your persona.",
    body: "Your voice becomes a written persona brief and a first-read moodboard. A quiet portrait of how your home wants to feel.",
    icon: Sparkles,
  },
  {
    title: "if we get you, here's the plan.",
    body: "A recommended plan lands in your private report with pricing, scope, and next steps. Three tiers, one clear pick.",
    icon: Compass,
  },
  {
    title: "sign up, meet your team.",
    body: "Send the advance and get allocated to a dedicated design and project management crew. Real people who show up on time.",
    icon: Users,
  },
  {
    title: "kick off. bring it to life.",
    body: "Your Folk Studio becomes the shared home for every decision: moodboards, layouts, renders and materials. From first mood to install day.",
    icon: Home,
  },
];


function JourneyStep({ num, title, body, Icon, isLast }) {
  return (
    <li
      data-testid={`journey-step-${num}`}
      className={`folk-journey-step${isLast ? " is-last" : ""}`}
    >
      <div className="folk-journey-rail" aria-hidden="true">
        <div className="folk-journey-dot">
          {Icon && <Icon size={20} strokeWidth={1.4} />}
        </div>
        {!isLast && <div className="folk-journey-line" />}
      </div>
      <div className="folk-journey-body">
        <div className="folk-journey-num">Step {num}</div>
        <h3 className="folk-serif folk-journey-title">{title}</h3>
        <p className="folk-journey-copy">{body}</p>
      </div>
    </li>
  );
}

function FeatureCard({ index, title, body }) {
  return (
    <div
      data-testid={`feature-card-${index}`}
      className="landing-feature-card"
      style={{
        background: "#FFFFFF",
        border: "1.5px solid rgba(15, 58, 84, 0.18)",
        borderRadius: 18,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        boxShadow: "0 8px 24px rgba(15, 58, 84, 0.08)",
      }}
    >
      <div style={{ fontSize: 11.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "#0F3A54", fontWeight: 700 }}>
        {index}
      </div>
      <h3 className="folk-serif" style={{ fontSize: 21, lineHeight: 1.2, margin: 0, color: "#0F3A54" }}>{title}</h3>
      <p style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.55, color: "#2A2828", margin: 0 }}>{body}</p>
    </div>
  );
}


function DesignerLeadSection() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [studio, setStudio] = useState("");
  const [notes, setNotes] = useState("");
  const [renderFiles, setRenderFiles] = useState([]);
  const [boqFiles, setBoqFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const renderInputRef = useRef(null);
  const boqInputRef = useRef(null);

  const addRenderFiles = (files) => setRenderFiles((prev) => [...prev, ...Array.from(files || [])]);
  const addBoqFiles = (files) => setBoqFiles((prev) => [...prev, ...Array.from(files || [])]);
  const removeAt = (setter, idx) => setter((prev) => prev.filter((_, i) => i !== idx));

  const totalFiles = renderFiles.length + boqFiles.length;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email so we can reply.");
      return;
    }
    if (totalFiles === 0) {
      toast.error("Please attach at least one render or BOQ file.");
      return;
    }
    if (totalFiles > 30) {
      toast.error("Please attach at most 30 files.");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("email", email.trim());
      form.append("phone", phone.trim());
      form.append("studio", studio.trim());
      form.append("notes", notes.trim());
      renderFiles.forEach((f) => form.append("renders", f));
      boqFiles.forEach((f) => form.append("boq", f));
      await axios.post(`${API}/designer-leads`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Received — our team will reply within 72 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="for-designers"
      data-testid="designer-lead-section"
      style={{ background: "#0F3A54", color: "#FFFBF3", padding: "96px 48px" }}
    >
      <div className="folk-designer-grid" style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)", gap: 64, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.28em", textTransform: "uppercase", color: "#BADDF0", fontWeight: 600, marginBottom: 14 }}>
            For interior designers
          </div>
          <h2
            className="folk-serif"
            style={{ fontSize: "clamp(30px, 4.4vw, 48px)", lineHeight: 1.1, margin: "0 0 20px", color: "#FFFBF3", letterSpacing: "-0.028em", textTransform: "lowercase" }}
          >
            already designing? let folk source your <em style={{ fontStyle: "italic" }}>vision</em>.
          </h2>
          <p style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 16.5, lineHeight: 1.6, color: "#E4EEF5", margin: "0 0 20px", maxWidth: 480 }}>
            Working on a project and want to source or map your BOQ, or bring a set of renders to life?
            Share the files, tell us what you need, and our team gets back within <b>72 hours</b> with pricing,
            shoppable matches from the Folk catalogue, and a plan to make it real.
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {["Share renders (JPG/PNG/PDF) and your BOQ (PDF, CSV, XLSX)", "We match items against the live Folk catalogue", "Custom, made-to-order pieces sourced on request", "One reply, within 72 hours, from a real designer"].map((line) => (
              <li key={line} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontFamily: "Host Grotesk, sans-serif", fontSize: 14.5, lineHeight: 1.5, color: "#D6E5EE" }}>
                <span style={{ color: "#BADDF0", fontWeight: 700, marginTop: 2 }}>·</span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ background: "#FFFBF3", color: "#1B1A1A", borderRadius: 20, padding: 28, boxShadow: "0 20px 40px rgba(0,0,0,0.16)" }}>
          {done ? (
            <div data-testid="designer-lead-success" style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "flex-start" }}>
              <div style={{ fontSize: 11.5, letterSpacing: "0.28em", textTransform: "uppercase", color: "#68280E", fontWeight: 700 }}>Got it</div>
              <h3 className="folk-serif" style={{ fontSize: 28, lineHeight: 1.15, margin: 0, textTransform: "lowercase", letterSpacing: "-0.028em" }}>
                thanks, {(name.split(" ")[0] || "friend").toLowerCase()} · files landed safely.
              </h3>
              <p style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.6, color: "#2A2828", margin: 0 }}>
                A real Folk designer will reply to <b>{email}</b> within 72 hours with pricing, matches from our
                catalogue, and next steps. Keep an eye on your inbox (and spam, just in case).
              </p>
              <button
                type="button"
                data-testid="designer-lead-reset"
                onClick={() => {
                  setDone(false); setName(""); setEmail(""); setPhone(""); setStudio(""); setNotes(""); setRenderFiles([]); setBoqFiles([]);
                }}
                className="folk-pill"
                style={{ marginTop: 6 }}
              >
                Send another project →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 11.5, letterSpacing: "0.28em", textTransform: "uppercase", color: "#68280E", fontWeight: 700 }}>Share your project</div>
              <h3 className="folk-serif" style={{ fontSize: 24, lineHeight: 1.2, margin: 0, textTransform: "lowercase", letterSpacing: "-0.028em" }}>
                72-hour reply, from a real folk designer.
              </h3>
              <div className="folk-designer-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <LeadField label="Your name*" testId="designer-name" value={name} onChange={setName} placeholder="Anushree Kohli" />
                <LeadField label="Email*" testId="designer-email" value={email} onChange={setEmail} type="email" placeholder="you@studio.com" />
                <LeadField label="Phone" testId="designer-phone" value={phone} onChange={setPhone} placeholder="+91 ..." />
                <LeadField label="Studio (optional)" testId="designer-studio" value={studio} onChange={setStudio} placeholder="Studio name" />
              </div>
              <LeadTextarea label="Project notes" testId="designer-notes" value={notes} onChange={setNotes} placeholder="Space type, style, timeline, anything else we should know." />

              <FilePickerBlock
                label="Renders"
                hint="JPG, PNG, WebP or PDF · up to 25MB each"
                accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf"
                inputRef={renderInputRef}
                files={renderFiles}
                onAdd={addRenderFiles}
                onRemoveIdx={(i) => removeAt(setRenderFiles, i)}
                testId="designer-renders"
              />
              <FilePickerBlock
                label="BOQ"
                hint="PDF, CSV or XLSX · up to 25MB each"
                accept="application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv,.xlsx"
                inputRef={boqInputRef}
                files={boqFiles}
                onAdd={addBoqFiles}
                onRemoveIdx={(i) => removeAt(setBoqFiles, i)}
                testId="designer-boq"
              />

              <button
                type="submit"
                data-testid="designer-lead-submit"
                disabled={submitting}
                className="folk-pill folk-pill-lg"
                style={{ marginTop: 6, opacity: submitting ? 0.6 : 1, cursor: submitting ? "wait" : "pointer" }}
              >
                {submitting ? "Sending files..." : `Send to Folk (${totalFiles} file${totalFiles === 1 ? "" : "s"}) →`}
              </button>
              <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "#5A5757" }}>
                By sending, you agree to a Folk designer contacting you at the email above.
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function LeadField({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontFamily: "Host Grotesk, sans-serif" }}>
      <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700 }}>{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="folk-input"
        style={{ padding: "10px 14px", fontSize: 14, borderRadius: 999, border: "1.5px solid rgba(15,58,84,0.25)", background: "#FFFFFF" }}
      />
    </label>
  );
}

function LeadTextarea({ label, value, onChange, placeholder, testId }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontFamily: "Host Grotesk, sans-serif" }}>
      <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700 }}>{label}</span>
      <textarea
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        style={{ padding: "12px 14px", fontSize: 14, borderRadius: 14, border: "1.5px solid rgba(15,58,84,0.25)", background: "#FFFFFF", resize: "vertical", fontFamily: "inherit" }}
      />
    </label>
  );
}

function FilePickerBlock({ label, hint, accept, inputRef, files, onAdd, onRemoveIdx, testId }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, fontFamily: "Host Grotesk, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 11.5, color: "#5A5757" }}>{hint}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        onChange={(e) => { onAdd(e.target.files); if (inputRef.current) inputRef.current.value = ""; }}
        data-testid={`${testId}-input`}
        style={{ display: "none" }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={`${testId}-btn`}
        style={{
          padding: "10px 16px", borderRadius: 12,
          border: "1.5px dashed rgba(15,58,84,0.4)",
          background: "#F3F8FB", color: "#0F3A54",
          fontSize: 13.5, fontWeight: 600, cursor: "pointer",
          textAlign: "left",
        }}
      >
        + Add {label.toLowerCase()} files
      </button>
      {files.length > 0 && (
        <ul data-testid={`${testId}-list`} style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, background: "#F3F8FB", borderRadius: 10, padding: "8px 12px", fontSize: 13 }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span style={{ color: "#5A5757", fontSize: 12 }}>{Math.round(f.size / 1024)} KB</span>
              <button
                type="button"
                onClick={() => onRemoveIdx(i)}
                data-testid={`${testId}-remove-${i}`}
                style={{ background: "transparent", border: "none", color: "#B04A2E", cursor: "pointer", fontSize: 13 }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
